/**
 * `LauncherBubble` — the floating launcher button shown while the panel is
 * collapsed (ARCHITECTURE §7). Tapping it expands the widget. Brand-colored via
 * the `--lc-*` vars, and now honors the org's launcher badge: a preset glyph
 * (shared icon library), a custom uploaded image, and the configured size.
 *
 * Optionally shows a one-line greeting teaser next to the bubble when the org
 * enables "show messages on load" — a recognizable "nudge" pattern.
 *
 * The bubble also carries its own × so the VISITOR can put the launcher away for
 * this browsing session (`onDismiss`) — same visual language as the teaser's ×,
 * one step up in size because it is the more consequential of the two. It is only
 * rendered when the embed loader has said it can actually take the frame off the
 * host page (see `loaderBridge`); `onDismiss` is undefined otherwise.
 */

import type { OrgTheme } from '@flozic/shared/types';
import { launcherIcon, presetSvg } from '@flozic/shared/theme';

export interface LauncherBubbleProps {
  onOpen: () => void;
  theme?: OrgTheme;
  /** Greeting teaser text shown beside the bubble (when the org opts in). */
  teaser?: string | null;
  onDismissTeaser?: () => void;
  /** Visitor hid the launcher itself (not just the teaser) for this browsing session. */
  onDismiss?: () => void;
}

/** Launcher diameter (px) for each configured size. */
const BADGE_PX = { small: 48, medium: 56, large: 64 } as const;

/**
 * Shared look for the two × controls (teaser and launcher).
 *
 * Deliberately NOT brand-colored: both discs are drawn partly over the CUSTOMER's page,
 * whose background we know nothing about, and the launcher's sits partly over the org's
 * own badge — which can be a custom uploaded image of any lightness. A dark scrim carries
 * the white glyph on light backdrops, and the light ring outlines the disc on dark ones,
 * so the control survives every combination without knowing any of them.
 */
const DISMISS_DISC =
  'rounded-full bg-black/70 text-white shadow-sm ring-1 ring-white/80 transition-colors ' +
  'hover:bg-black/85 focus-visible:ring-2 focus-visible:ring-white';

export function LauncherBubble({
  onOpen,
  theme,
  teaser,
  onDismissTeaser,
  onDismiss,
}: LauncherBubbleProps) {
  const size = BADGE_PX[theme?.badgeSize ?? 'medium'];
  const isCustom = theme?.badgeStyle === 'custom' && !!theme?.badgeIconUrl;
  const glyph = launcherIcon(theme?.badgeStyle);

  return (
    <div className="flex items-end justify-end gap-2">
      {teaser ? (
        <div className="relative mb-1 max-w-[200px] rounded-2xl rounded-br-sm bg-lc-bubble-in px-3 py-2 text-sm text-lc-text shadow-lg">
          {teaser}
          {/* Paints 20px, but the `after` box takes clicks out to 24px — the smallest
              target a thumb can be asked to hit. The offsets keep the painted disc exactly
              where it has always sat despite the larger hit area. */}
          {onDismissTeaser ? (
            <button
              type="button"
              aria-label="Dismiss message"
              onClick={onDismissTeaser}
              className={`absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center text-[11px] leading-none after:absolute after:-inset-0.5 after:rounded-full after:content-[''] ${DISMISS_DISC}`}
            >
              ×
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="relative shrink-0">
        <button
          type="button"
          aria-label="Open chat"
          onClick={onOpen}
          style={{ width: size, height: size }}
          className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-lc-primary text-lc-on-primary shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
          {isCustom ? (
            <img
              src={theme!.badgeIconUrl}
              alt=""
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span
              aria-hidden="true"
              dangerouslySetInnerHTML={{ __html: presetSvg(glyph, Math.round(size * 0.5)) }}
            />
          )}
        </button>
        {/* Put the launcher away.
            • Sits AFTER the launcher in DOM order so Tab reaches "Open chat" first —
              hiding chat should never be the first thing a keyboard user lands on.
            • Painted at 18px, but `after` stretches the hit box to 24px. Most widget
              traffic is touch, and 18px is below what a thumb can be asked to hit.
            • Pushed 4px OUT of the bubble's box, which is as far as it can go: the
              collapsed frame is 96px with `overflow:hidden` and the container below pads
              it by 8px, so the 24px hit box overhangs by 7px and just clears. That 4px is
              what keeps a fat finger off it — it doubles the gap between the hit box and
              the launcher's centre (10px at the 48px badge, where it is tightest) and
              halves the bite the box takes out of the tappable circle. The padding is a
              constant, so the geometry holds at 48/56/64 alike.
            • Always visible rather than hover-revealed: touch devices have no hover, so a
              hover-only control would be one most visitors never get. */}
        {onDismiss ? (
          <button
            type="button"
            aria-label="Hide the chat button"
            title="Hide the chat button"
            onClick={onDismiss}
            className={`absolute -right-1 -top-1 grid h-[18px] w-[18px] place-items-center text-[13px] leading-none after:absolute after:-inset-[3px] after:rounded-full after:content-[''] ${DISMISS_DISC}`}
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * `LauncherRestoreArrow` — what remains after the visitor dismisses the launcher: a small
 * brand-colored disc with an upward chevron, sitting where the bubble used to be. One tap
 * brings the launcher back.
 *
 * This is the dismissal's other half. The frame never fully leaves the host page any more
 * (the loader shrinks it to a 44px dock around this control), because a dismissal with no
 * visible way back reads as "the chat is gone forever" — the exact bug report that led
 * here. Brand-colored on purpose, unlike the × discs: it must read as "the chat lives
 * here, tucked away", not as generic page furniture.
 *
 * Sized to its 36px box inside the 44px dock (p-1 in the container) — comfortably past the
 * 24px minimum touch target, and the docked frame leaves nothing else to mis-tap.
 */
export function LauncherRestoreArrow({ onRestore }: { onRestore: () => void }) {
  return (
    <button
      type="button"
      aria-label="Show the chat button"
      title="Show the chat button"
      onClick={onRestore}
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-lc-primary text-lc-on-primary shadow-lg ring-1 ring-white/60 transition-transform hover:scale-110 active:scale-95 focus-visible:ring-2 focus-visible:ring-white"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 14.5 12 7.5l7 7" />
      </svg>
    </button>
  );
}
